'use client';

import { AiSparklesIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/ai-sparkles-icon';
import { BlockquoteIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/blockquote-icon';
import { ChevronRightIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/chevron-right-icon';
import { CodeBlockIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/code-block-icon';
import { HeadingOneIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/heading-one-icon';
import { HeadingThreeIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/heading-three-icon';
import { HeadingTwoIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/heading-two-icon';
import { ListIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/list-icon';
import { ListOrderedIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/list-ordered-icon';
import { ListTodoIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/list-todo-icon';
import { MathIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/math-icon';
import { MinusIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/minus-icon';
import { SmilePlusIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/smile-plus-icon';
import { TableIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/table-icon';
import { TypeIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/type-icon';
import type { SuggestionItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-utils/suggestion-menu';
import { addEmojiTrigger } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/emoji-trigger-button';
import {
  findSelectionPosition,
  hasContentAbove,
} from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-advanced-utils';
import { isExtensionAvailable, isNodeInSchema } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useCallback } from 'react';

type IconProps = React.SVGProps<SVGSVGElement>;
type IconComponent = ({ className, ...props }: IconProps) => React.ReactElement;

export interface SlashMenuItemMetadata {
  title: string;
  subtext?: string;
  keywords?: string[];
  badge?: React.MemoExoticComponent<IconComponent> | React.FC<IconProps> | string;
  group?: string;
}

export interface SlashMenuItemImplementation {
  check: (editor: Editor) => boolean;
  action: ({ editor }: { editor: Editor }) => void;
}

export interface SlashMenuItem {
  metadata: SlashMenuItemMetadata;
  implementation: SlashMenuItemImplementation;
}

export interface SlashMenuConfig {
  enabledItems?: SlashMenuItemType[];
  customItems?: SuggestionItem[];
  itemGroups?: {
    [key in SlashMenuItemType]?: string;
  };
  showGroups?: boolean;
  itemsMetadata?: Record<string, SlashMenuItemMetadata>;
  itemImplementations?: Record<string, SlashMenuItemImplementation>;
}

const texts: Record<string, SlashMenuItemMetadata> = {
  // AI
  continue_writing: {
    title: 'Continue Writing',
    subtext: 'Continue writing from the current position',
    keywords: ['continue', 'write', 'continue writing', 'ai'],
    badge: AiSparklesIcon,
    group: 'AI',
  },
  ai_ask_button: {
    title: 'Ask AI',
    subtext: 'Ask AI to generate content',
    keywords: ['ai', 'ask', 'generate'],
    badge: AiSparklesIcon,
    group: 'AI',
  },

  // Style
  text: {
    title: 'Text',
    subtext: 'Regular text paragraph',
    keywords: ['p', 'paragraph', 'text'],
    badge: TypeIcon,
    group: 'Style',
  },
  heading_1: {
    title: 'Heading 1',
    subtext: 'Top-level heading',
    keywords: ['h', 'heading1', 'h1'],
    badge: HeadingOneIcon,
    group: 'Style',
  },
  heading_2: {
    title: 'Heading 2',
    subtext: 'Key section heading',
    keywords: ['h2', 'heading2', 'subheading'],
    badge: HeadingTwoIcon,
    group: 'Style',
  },
  heading_3: {
    title: 'Heading 3',
    subtext: 'Subsection and group heading',
    keywords: ['h3', 'heading3', 'subheading'],
    badge: HeadingThreeIcon,
    group: 'Style',
  },
  bullet_list: {
    title: 'Bullet List',
    subtext: 'List with unordered items',
    keywords: ['ul', 'li', 'list', 'bulletlist', 'bullet list'],
    badge: ListIcon,
    group: 'Style',
  },
  ordered_list: {
    title: 'Numbered List',
    subtext: 'List with ordered items',
    keywords: ['ol', 'li', 'list', 'numberedlist', 'numbered list'],
    badge: ListOrderedIcon,
    group: 'Style',
  },
  task_list: {
    title: 'To-do list',
    subtext: 'List with tasks',
    keywords: ['tasklist', 'task list', 'todo', 'checklist'],
    badge: ListTodoIcon,
    group: 'Style',
  },
  quote: {
    title: 'Blockquote',
    subtext: 'Blockquote block',
    keywords: ['quote', 'blockquote'],
    badge: BlockquoteIcon,
    group: 'Style',
  },
  code_block: {
    title: 'Code Block',
    subtext: 'Code block with syntax highlighting',
    keywords: ['code', 'pre'],
    badge: CodeBlockIcon,
    group: 'Style',
  },
  toggle: {
    title: 'Toggle',
    subtext: 'Collapsible section',
    keywords: ['toggle', 'collapsible', 'details', 'accordion', 'expand', 'collapse'],
    badge: ChevronRightIcon,
    group: 'Style',
  },

  // Insert
  // mention: {
  //   title: 'Mention',
  //   subtext: 'Mention a user or item',
  //   keywords: ['mention', 'user', 'item', 'tag'],
  //   badge: AtSignIcon,
  //   group: 'Insert',
  // },
  emoji: {
    title: 'Emoji',
    subtext: 'Insert an emoji',
    keywords: ['emoji', 'emoticon', 'smiley'],
    badge: SmilePlusIcon,
    group: 'Insert',
  },
  table: {
    title: 'Table',
    subtext: 'Insert a table',
    keywords: ['table', 'insertTable'],
    badge: TableIcon,
    group: 'Insert',
  },
  divider: {
    title: 'Separator',
    subtext: 'Horizontal line to separate content',
    keywords: ['hr', 'horizontalRule', 'line', 'separator'],
    badge: MinusIcon,
    group: 'Insert',
  },
  inline_math: {
    title: 'Math',
    subtext: 'Insert an inline mathematical expression',
    keywords: ['math', 'latex', 'katex', 'equation', 'formula', 'inline'],
    badge: MathIcon,
    group: 'Insert',
  },

  // Upload
  // image: {
  //   title: 'Image',
  //   subtext: 'Resizable image with caption',
  //   keywords: ['image', 'imageUpload', 'upload', 'img', 'picture', 'media', 'url'],
  //   badge: ImageIcon,
  //   group: 'Upload',
  // },
};

export type SlashMenuItemType = keyof typeof texts;

const getItemImplementations = (): Record<string, SlashMenuItemImplementation> => {
  return {
    // AI
    continue_writing: {
      check: (editor: Editor) => {
        const { hasContent } = hasContentAbove(editor);
        const extensionsReady = isExtensionAvailable(editor, ['ai', 'aiAdvanced']);
        return extensionsReady && hasContent;
      },
      action: ({ editor }: { editor: Editor }) => {
        const editorChain = editor.chain().focus();

        const nodeSelectionPosition = findSelectionPosition({ editor });

        if (nodeSelectionPosition !== null) {
          editorChain.setNodeSelection(nodeSelectionPosition);
        }

        editorChain.run();

        editor.chain().focus().aiGenerationShow().run();

        requestAnimationFrame(() => {
          const { hasContent, content } = hasContentAbove(editor);

          const snippet = content.length > 500 ? `...${content.slice(-500)}` : content;

          const prompt = hasContent
            ? `Context: ${snippet}\n\nContinue writing from where the text above ends. Write ONLY ONE SENTENCE. DONT REPEAT THE TEXT.`
            : 'Start writing a new paragraph. Write ONLY ONE SENTENCE.';

          editor
            .chain()
            .focus()
            .aiTextPrompt({
              stream: true,
              format: 'rich-text',
              text: prompt,
            })
            .run();
        });
      },
    },
    ai_ask_button: {
      check: (editor: Editor) => isExtensionAvailable(editor, ['ai', 'aiAdvanced']),
      action: ({ editor }: { editor: Editor }) => {
        const editorChain = editor.chain().focus();

        const nodeSelectionPosition = findSelectionPosition({ editor });

        if (nodeSelectionPosition !== null) {
          editorChain.setNodeSelection(nodeSelectionPosition);
        }

        editorChain.run();

        editor.chain().focus().aiGenerationShow().run();
      },
    },

    // Style
    text: {
      check: (editor: Editor) => isNodeInSchema('paragraph', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setParagraph().run();
      },
    },
    heading_1: {
      check: (editor: Editor) => isNodeInSchema('heading', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 1 }).run();
      },
    },
    heading_2: {
      check: (editor: Editor) => isNodeInSchema('heading', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 2 }).run();
      },
    },
    heading_3: {
      check: (editor: Editor) => isNodeInSchema('heading', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 3 }).run();
      },
    },
    bullet_list: {
      check: (editor: Editor) => isNodeInSchema('bulletList', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleBulletList().run();
      },
    },
    ordered_list: {
      check: (editor: Editor) => isNodeInSchema('orderedList', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleOrderedList().run();
      },
    },
    task_list: {
      check: (editor: Editor) => isNodeInSchema('taskList', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleTaskList().run();
      },
    },
    quote: {
      check: (editor: Editor) => isNodeInSchema('blockquote', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleBlockquote().run();
      },
    },
    code_block: {
      check: (editor: Editor) => isNodeInSchema('codeBlock', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleNode('codeBlock', 'paragraph').run();
      },
    },
    toggle: {
      check: (editor: Editor) => isNodeInSchema('details', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setDetails().run();
      },
    },

    // Insert
    // mention: {
    //   check: (editor: Editor) => isExtensionAvailable(editor, ['mention', 'mentionAdvanced']),
    //   action: ({ editor }: { editor: Editor }) => addMentionTrigger(editor),
    // },
    emoji: {
      check: (editor: Editor) => isExtensionAvailable(editor, ['emoji', 'emojiPicker']),
      action: ({ editor }: { editor: Editor }) => addEmojiTrigger(editor),
    },
    divider: {
      check: (editor: Editor) => isNodeInSchema('horizontalRule', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setHorizontalRule().run();
      },
    },
    table: {
      check: (editor: Editor) => isNodeInSchema('table', editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertTable({
            rows: 3,
            cols: 3,
            withHeaderRow: false,
          })
          .run();
      },
    },
    inline_math: {
      check: (editor: Editor) => isNodeInSchema('inlineMath', editor),
      action: ({ editor }: { editor: Editor }) => {
        const latex = prompt('Enter inline math (LaTeX):');
        if (latex) {
          editor.chain().focus().insertInlineMath({ latex }).run();
        }
      },
    },

    // Upload
    // image: {
    //   check: (editor: Editor) => isNodeInSchema('image', editor),
    //   action: ({ editor }: { editor: Editor }) => {
    //     editor
    //       .chain()
    //       .focus()
    //       .insertContent({
    //         type: 'imageUpload',
    //       })
    //       .run();
    //   },
    // },
  };
};

function organizeItemsByGroups(items: SuggestionItem[], showGroups: boolean): SuggestionItem[] {
  if (!showGroups) {
    return items.map((item) => ({ ...item, group: '' }));
  }

  const groups: { [groupLabel: string]: SuggestionItem[] } = {};

  // Group items
  items.forEach((item) => {
    const groupLabel = item.group || '';
    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }
    groups[groupLabel].push(item);
  });

  // Flatten groups in order (this maintains the visual order for keyboard navigation)
  const organizedItems: SuggestionItem[] = [];
  Object.entries(groups).forEach(([, groupItems]) => {
    organizedItems.push(...groupItems);
  });

  return organizedItems;
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config?: SlashMenuConfig) {
  const getSlashMenuItems = useCallback(
    (editor: Editor) => {
      const items: SuggestionItem[] = [];

      const enabledItems = config?.enabledItems || (Object.keys(texts) as SlashMenuItemType[]);
      const showGroups = config?.showGroups !== false;

      const itemImplementations = getItemImplementations();
      const customItemsMetadata = config?.itemsMetadata || {};
      const customItemImplementations = config?.itemImplementations || {};

      // Merge built-in and custom metadata
      const allItemsMetadata = { ...texts, ...customItemsMetadata };
      // Merge built-in and custom implementations
      const allItemImplementations = { ...itemImplementations, ...customItemImplementations };

      const customItems = Object.keys(customItemsMetadata).filter(
        (itemType) => !enabledItems.includes(itemType as SlashMenuItemType),
      );
      const allItemTypes = [...enabledItems, ...customItems];

      allItemTypes.forEach((itemType) => {
        const itemImpl = allItemImplementations[itemType];
        const itemText = allItemsMetadata[itemType];

        if (itemImpl && itemText && itemImpl.check(editor)) {
          const item: SuggestionItem = {
            onSelect: ({ editor }) => itemImpl.action({ editor }),
            ...itemText,
          };

          if (config?.itemGroups?.[itemType]) {
            item.group = config.itemGroups[itemType];
          } else if (!showGroups) {
            item.group = '';
          }

          items.push(item);
        }
      });

      if (config?.customItems) {
        items.push(...config.customItems);
      }

      // Reorganize items by groups to ensure keyboard navigation works correctly
      return organizeItemsByGroups(items, showGroups);
    },
    [config],
  );

  return {
    getSlashMenuItems,
    config,
  };
}
